import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, X } from "lucide-react";

function SimCard({ item, index, onOpen }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.7, delay: index * 0.08, ease: [0.22, 1, 0.36, 1] }}
      className="group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0c0c0c]"
      data-testid={`simulator-card-${item.slug}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden">
        <img
          src={item.image_url}
          alt={item.name}
          loading="lazy"
          className="h-full w-full object-cover transform-gpu group-hover:scale-110 transition-transform duration-[900ms] ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-transparent" />
        <div className={`absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${item.accent === "blue" ? "bg-[#008CFF]/10" : "bg-[#E31B23]/10"}`} />
        <div className="absolute top-5 left-5 overline text-xs text-white/70">0{index + 1} / SIMULADOR</div>
        <div className="absolute top-5 right-5">
          <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] tracking-widest uppercase ${
            item.accent === "blue"
              ? "border-[#008CFF]/50 text-[#008CFF] bg-[#008CFF]/5"
              : "border-[#E31B23]/50 text-[#E31B23] bg-[#E31B23]/5"
          }`}>
            {item.accent === "blue" ? "TECH" : "RACE"}
          </span>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="overline text-neutral-400 mb-2">{item.subtitle}</div>
          <h3 className="font-display font-black text-2xl md:text-3xl leading-tight text-white">
            {item.name}
          </h3>
          <p className="mt-3 text-sm text-neutral-300 line-clamp-2">{item.description}</p>
          <button
            onClick={() => onOpen(item)}
            className={`mt-5 inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold uppercase tracking-widest ${
              item.accent === "blue"
                ? "border border-[#008CFF]/60 text-white glow-blue"
                : "bg-[#E31B23] text-white glow-red"
            }`}
            data-testid={`simulator-saibamais-${item.slug}`}
          >
            Saiba mais
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function Simulators({ items = [], onOpenQuote }) {
  const [active, setActive] = useState(null);

  return (
    <section id="simuladores" className="relative py-28 md:py-40 bg-[#070707]" data-testid="simulators-section">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="flex items-end justify-between gap-8 flex-wrap mb-14">
          <div>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-[1px] w-10 bg-[#E31B23]" />
              <span className="overline">Frota profissional</span>
            </div>
            <h2 className="font-display font-black text-5xl md:text-6xl lg:text-7xl leading-[0.95]">
              <span className="text-white">NOSSOS </span>
              <span className="neon-red">SIMULADORES</span>
            </h2>
          </div>
          <p className="max-w-md text-neutral-400 leading-relaxed">
            Equipamentos de nível de competição para eventos que precisam impressionar. Escolha a experiência ideal para o seu público.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((s, i) => (
            <SimCard key={s.id} item={s} index={i} onOpen={setActive} />
          ))}
        </div>
      </div>

      {/* Modal */}
      {active && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center p-4 md:p-8" data-testid="simulator-modal">
          <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={() => setActive(null)} />
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            className="relative max-w-4xl w-full max-h-[90vh] overflow-y-auto rounded-2xl border border-white/10 bg-[#0a0a0a] grid md:grid-cols-2"
          >
            <div className="relative aspect-video md:aspect-auto">
              <img src={active.image_url} alt={active.name} className="w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#0a0a0a]/80" />
            </div>
            <div className="p-8 md:p-10">
              <button className="absolute top-4 right-4 p-2 rounded-full border border-white/10 hover:border-white/40" onClick={() => setActive(null)} data-testid="close-modal-btn">
                <X className="w-4 h-4" />
              </button>
              <div className="overline text-[#E31B23] mb-2">{active.subtitle}</div>
              <h3 className="font-display font-black text-3xl md:text-4xl text-white">{active.name}</h3>
              <p className="mt-4 text-neutral-300 leading-relaxed">{active.description}</p>
              <div className="mt-6 grid grid-cols-1 gap-2">
                {(active.features || []).map((f) => (
                  <div key={f} className="flex items-center gap-3 text-sm text-neutral-200">
                    <Check className="w-4 h-4 text-[#008CFF]" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={() => { setActive(null); onOpenQuote(active.slug); }}
                className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E31B23] px-6 py-3.5 text-sm font-semibold text-white uppercase tracking-widest glow-red"
                data-testid="modal-quote-btn"
              >
                Solicitar orçamento
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </section>
  );
}
